import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { AuthShell } from '@/components/auth/auth-shell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';

/**
 * Human copy for the reset failures a user can actually hit. Known codes map
 * to fixed strings; the fallback renders better-auth's own (server-controlled,
 * React-escaped) message — never any URL-supplied value.
 */
function resetErrorMessage(code: string | undefined, fallback?: string): string {
  switch (code) {
    case 'INVALID_TOKEN':
      return 'This reset link is invalid or has expired. Request a new one.';
    case 'PASSWORD_TOO_SHORT':
      return 'Password must be at least 8 characters.';
    default:
      return fallback ?? 'Something went wrong. Please try again.';
  }
}

/**
 * /reset-password — set a new password (plan 2026-07-24-password-reset).
 *
 * Arrival shapes, all from better-auth's GET /reset-password/:token callback
 * (it validates WITHOUT consuming — the token dies only on a successful POST):
 *   ?token=<valid>     → the form
 *   ?error=INVALID_TOKEN → expired/unknown link
 *   (neither)          → stray visit; bounce to /forgot-password
 *
 * Success covers both audiences: credential users reset, social-only users
 * set their FIRST password (the credential account is created server-side).
 * Post-reset the user signs in fresh — all sessions were revoked
 * (revokeSessionsOnPasswordReset, plan D1) and no grant params survive the
 * email round-trip (plan D3).
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string; error?: string };
  const token = search.token ?? '';
  const linkError = search.error;
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Stray visit (no token, no error): nothing to do here.
  useEffect(() => {
    if (!token && !linkError) void navigate({ to: '/forgot-password' });
  }, [token, linkError, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const { error: resetError } = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (resetError) {
        setError(resetErrorMessage(resetError.code, resetError.message));
      } else {
        setDone(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const invalidLink = linkError === 'INVALID_TOKEN';

  return (
    <div className="flex min-h-screen flex-col">
      <AuthShell>
        {done ? (
          <div>
            <div className="mb-6">
              <h1 className="text-lg font-semibold">Password updated</h1>
              <p className="mt-1 text-sm text-faint">You can now sign in with your new password.</p>
            </div>
            <div className="flex items-center justify-center py-4 text-faint">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <Link to="/login">
              <Button size="cta" className="w-full">
                Continue to sign in
              </Button>
            </Link>
          </div>
        ) : invalidLink ? (
          <div>
            <div className="mb-6">
              <h1 className="text-lg font-semibold">Link expired</h1>
              <p className="mt-1 text-sm text-faint">
                This reset link is invalid or has expired. Links are valid for 1 hour.
              </p>
            </div>
            <Link to="/forgot-password">
              <Button size="cta" className="w-full">
                Request a new link
              </Button>
            </Link>
            <p className="mt-6 text-center text-sm text-faint">
              <Link to="/login" className="font-semibold text-foreground hover:underline">
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <h1 className="text-lg font-semibold">Set a new password</h1>
              <p className="mt-1 text-sm text-faint">At least 8 characters.</p>
            </div>
            <form
              onSubmit={(e) => {
                void handleSubmit(e);
              }}
              className="space-y-4"
            >
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="At least 8 characters"
                />
              </div>
              <Button type="submit" size="cta" className="w-full" disabled={loading}>
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </div>
        )}
      </AuthShell>
    </div>
  );
}
